import { FC, useEffect } from "react";
import { useForm, FormProvider, useFormState } from "react-hook-form";
import { Link } from "react-router-dom";
import { ArrowLeft, Loader2, Trash2 } from "lucide-react";
import { isEmpty } from "lodash";
import { Button } from "src/components/ui/button";
import { Input } from "src/components/ui/input";
import {
	FormField,
	FormItem,
	FormLabel,
	FormControl
} from "src/components/ui/form";
import PermissionTransferList from "../PermissionTransferList/PermissionTransferList";

interface Props {
	defaultValues: RoleFormValues;
	availablePermissions: string[];
	onSubmit: (data: RoleFormValues) => void;
	isSubmitting?: boolean;
	isEdit?: boolean;
	isNameReadOnly?: boolean;
	onDelete?: () => void;
	backPath: string;
}

const RoleForm: FC<Props> = ({
	defaultValues,
	availablePermissions,
	onSubmit,
	isSubmitting = false,
	isEdit = false,
	isNameReadOnly = false,
	onDelete,
	backPath
}) => {
	const methods = useForm<RoleFormValues>({
		mode: "onChange",
		reValidateMode: "onChange",
		defaultValues
	});

	const { reset, control, handleSubmit, setValue, watch } = methods;
	const { isDirty, errors } = useFormState({ control });

	const selectedPermissions = watch("permissions");

	const isSubmitBtnDisabled = !isDirty || !isEmpty(errors) || isSubmitting;

	useEffect(() => {
		reset(defaultValues);
	}, [defaultValues, reset]);

	const handlePermissionsChange = (selected: string[]) => {
		setValue("permissions", selected, { shouldDirty: true });
	};

	const handleKeyDown = (e: React.KeyboardEvent) => {
		if (e.key === "Enter" && e.target instanceof HTMLInputElement) {
			e.preventDefault();
		}
	};

	return (
		<form onSubmit={handleSubmit(onSubmit)} onKeyDown={handleKeyDown}>
			<FormProvider {...methods}>
				<div className="flex flex-col gap-5">
					<div className="rounded-xl border border-border bg-subtle shadow-sm p-6">
						<label className="font-heading text-lg font-medium text-foreground mb-4 block">
							{isEdit ? "Edit Role" : "New Role"}
						</label>

						<div className="flex flex-col gap-5">
							<FormField
								control={control}
								name="name"
								rules={{ required: "Role name is required" }}
								render={({ field }) => (
									<FormItem>
										<FormLabel>Name</FormLabel>
										<FormControl>
											<div className="relative">
												<Input
													placeholder="Role name"
													{...field}
													readOnly={isNameReadOnly}
													className={
														isNameReadOnly
															? "bg-muted text-muted-foreground cursor-not-allowed"
															: ""
													}
												/>
												{errors.name && (
													<span className="absolute -top-2.5 right-2 bg-red-500 text-white text-[10px] px-1.5 py-0.5 rounded">
														{errors.name.message}
													</span>
												)}
											</div>
										</FormControl>
									</FormItem>
								)}
							/>

							<div>
								<label className="text-sm font-medium text-zinc-600 leading-none mb-2 block">
									Permissions
								</label>
								<PermissionTransferList
									availablePermissions={availablePermissions}
									selectedPermissions={selectedPermissions ?? []}
									onChange={handlePermissionsChange}
								/>
							</div>
						</div>
					</div>

					<div className="flex items-center justify-between pt-8 mt-2 border-t border-border">
						<Link
							to={backPath}
							className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors"
						>
							<ArrowLeft size={16} aria-hidden="true" />
							Back to Permissions
						</Link>

						<div className="flex items-center gap-3">
							{isEdit && onDelete && (
								<Button
									type="button"
									variant="outline"
									onClick={onDelete}
									className="text-destructive border-destructive/30 hover:bg-destructive/5"
								>
									<Trash2 className="h-4 w-4 mr-1.5" aria-hidden="true" />
									Delete
								</Button>
							)}
							<Button type="submit" disabled={isSubmitBtnDisabled}>
								{isSubmitting && (
									<Loader2
										className="h-4 w-4 mr-1.5 animate-spin"
										aria-hidden="true"
									/>
								)}
								{isEdit ? "Save" : "Create"}
							</Button>
						</div>
					</div>
				</div>
			</FormProvider>
		</form>
	);
};

export default RoleForm;
